/**
 * The dashboard: my active task, ready for you, waiting on you, recently
 * completed — and, for an Owner, the controls band with pending proposals.
 *
 * Whether the Owner band renders comes from the server's response shape
 * (`proposalsPending` is only populated for a caller the server recognises
 * as an administrator), mirrored by the profile flag for layout. Nothing on
 * this page decides authority; it draws what it was given.
 */

import { useCallback, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { fetchDashboard } from '../api.js';
import { TaskCard, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import { EmptyState } from '../ui.js';
import {
  FailurePanel,
  LoadingPanel,
  PageTitle,
  SectionHeading,
  useAsync,
} from './common.js';

import type { TaskSummary } from '../api.js';

function CardColumn({
  title,
  tasks,
  empty,
  onOpen,
}: {
  readonly title: string;
  readonly tasks: readonly TaskSummary[];
  readonly empty: string;
  readonly onOpen: (taskId: string) => void;
}): ReactNode {
  return (
    <section aria-label={title} style={{ minWidth: 0 }}>
      <SectionHeading>{title}</SectionHeading>
      {tasks.length === 0 ? (
        <EmptyState title={empty} compact />
      ) : (
        tasks.map((task) => <TaskCard key={task.id} task={task} onOpen={onOpen} />)
      )}
    </section>
  );
}

export function DashboardPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const navigate = useNavigate();
  const { state, reload } = useAsync(useCallback(() => fetchDashboard(), []));

  const open = useCallback(
    (taskId: string) => {
      void navigate(`/tasks/${taskId}`);
    },
    [navigate],
  );

  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed')
    return <FailurePanel failure={state.failure} onRetry={reload} />;

  const dashboard = state.value;
  const owner =
    session.status === 'authenticated' && session.profile.mayAdministerTasks;

  return (
    <>
      <PageTitle>{copy.dashboard.title}</PageTitle>

      {owner ? (
        <section
          aria-label={copy.dashboard.ownerControls}
          style={{
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-lg)',
            background: 'var(--surface-card)',
            // One of the two permitted emphasis cards on this screen.
            borderBlockStart: '2px solid transparent',
            borderImage: 'var(--gradient-tide-line) 1',
            borderImageWidth: '2px 0 0 0',
          }}
        >
          <SectionHeading>{copy.dashboard.ownerControls}</SectionHeading>
          <h3
            style={{
              font: 'var(--type-body-md)',
              color: 'var(--text-secondary)',
              marginBottom: 8,
            }}
          >
            {copy.dashboard.proposalsPending}
          </h3>
          {dashboard.proposalsPending.length === 0 ? (
            <EmptyState title={copy.dashboard.emptyProposals} compact />
          ) : (
            <div className="mj-dashboard-grid">
              {dashboard.proposalsPending.map((task) => (
                <TaskCard key={task.id} task={task} onOpen={open} />
              ))}
            </div>
          )}
        </section>
      ) : null}

      <div className="mj-dashboard-grid">
        <CardColumn
          title={copy.dashboard.myActiveTask}
          tasks={dashboard.myActiveTasks}
          empty={copy.dashboard.emptyActive}
          onOpen={open}
        />
        <CardColumn
          title={copy.dashboard.readyForYou}
          tasks={dashboard.readyForYou}
          empty={copy.dashboard.emptyReady}
          onOpen={open}
        />
        <CardColumn
          title={copy.dashboard.waitingOnYou}
          tasks={dashboard.waitingOnYou}
          empty={copy.dashboard.emptyWaiting}
          onOpen={open}
        />
      </div>

      <section
        aria-label={copy.dashboard.recentlyCompleted}
        style={{ marginTop: 'var(--space-lg)' }}
      >
        <SectionHeading>{copy.dashboard.recentlyCompleted}</SectionHeading>
        {dashboard.recentlyCompleted.length === 0 ? (
          <EmptyState title={copy.dashboard.emptyCompleted} compact />
        ) : (
          <div className="mj-dashboard-grid">
            {dashboard.recentlyCompleted.map((task) => (
              <TaskCard key={task.id} task={task} onOpen={open} />
            ))}
          </div>
        )}
      </section>

      <p style={{ marginTop: 'var(--space-lg)' }}>
        <Link
          to="/tasks"
          style={{ color: 'var(--text-link)', font: 'var(--type-body-md)' }}
        >
          {copy.dashboard.viewBoard}
        </Link>
      </p>
    </>
  );
}
